import {
  Project,
  getProjectByIdRequest,
  getProjectsRequest,
  createOrUpdateProjectRequest,
  getProjectsByUserPaginatedRequest,
  ProjectsPaginatedResponse,
  ProjectFilters,
} from '@models/project.model';
import { graphQlClient, graphQlUpload } from '@config/graphqlClient';
import ProjectsGraphQL from '@graphql/project.queries';

export type CreateProjectPayload = Omit<
  Project,
  | 'projectId'
  | 'createdAt'
  | 'projectManager'
  | 'superintendent'
  | 'superintendents'
  | 'client'
  | 'tasks'
> & {
  siteImage?: File | null;
};

export type UpdateProjectPayload = Omit<
  Project,
  | 'createdAt'
  | 'projectManager'
  | 'superintendent'
  | 'superintendents'
  | 'client'
  | 'tasks'
> & {
  siteImage?: File | null;
};

export const projectService = {
  async getProjectById(projectId: string): Promise<Project> {
    if (!projectId) throw new Error('projectId is required');

    const data: getProjectByIdRequest = await graphQlClient.request(
      ProjectsGraphQL.queries.getProjectById,
      { projectId },
    );

    if (!data?.getProjectById) throw new Error('Project not found');
    return data.getProjectById;
  },

  async getProjectsByUser(userId: string): Promise<Project[]> {
    if (!userId) throw new Error('userId is required');

    const data: any = await graphQlClient.request(
      ProjectsGraphQL.queries.getProjectsByUser,
      { userId },
    );

    return data.getProjectsByUser || [];
  },

  async getProjectsByUserWithStates(
    userId: string,
    states?: string[]
  ): Promise<Project[]> {
    if (!userId) throw new Error('userId is required');

    const data: any = await graphQlClient.request(
      ProjectsGraphQL.queries.getProjectsByUser,
      { userId, states: states && states.length > 0 ? states : undefined },
    );

    return data.getProjectsByUser || [];
  },

  async getProjectsByUserPaginated(
    userId: string,
    filters: ProjectFilters = {}
  ): Promise<ProjectsPaginatedResponse> {
    if (!userId) throw new Error('userId is required');

    const variables = {
      userId,
      page: filters.page || 1,
      limit: filters.limit || 10,
      search: filters.search || undefined,
      states: filters.states || undefined,
      sortBy: filters.sortBy || 'name',
    };

    const data: getProjectsByUserPaginatedRequest = await graphQlClient.request(
      ProjectsGraphQL.queries.getProjectsByUserPaginated,
      variables,
    );

    if (!data?.getProjectsByUserPaginated) {
      throw new Error('Failed to fetch paginated projects');
    }

    return data.getProjectsByUserPaginated;
  },

  async getProjects(): Promise<Project[]> {
    const data: getProjectsRequest = await graphQlClient.request(
      ProjectsGraphQL.queries.getProjects,
    );

    return data.getProjects || [];
  },

  async createProject(payload: CreateProjectPayload): Promise<Project> {
    // Normalize empty-string ids to null to avoid backend UUID parsing errors
    const normalizedPayload: CreateProjectPayload = {
      ...payload,
      clientId: (payload as any).clientId || null,
      projectManagerId: (payload as any).projectManagerId || null,
      superintendentIds: Array.isArray((payload as any).superintendentIds)
        ? ((payload as any).superintendentIds as string[]).filter(
            (id) => !!id && id !== '',
          )
        : [],
      reviewerEmployeeIds: Array.isArray((payload as any).reviewerEmployeeIds)
        ? ((payload as any).reviewerEmployeeIds as string[]).filter(
            (id) => !!id && id !== '',
          )
        : [],
    } as any;

    if (normalizedPayload.siteImage) {
      const { siteImage, ...rest } = normalizedPayload as any;
      const data: createOrUpdateProjectRequest = await graphQlUpload(
        siteImage,
        'project',
        ProjectsGraphQL.mutations.createProject,
        { ...rest },
      );
      if (!data?.createProject) throw new Error('Failed to create project');
      return data.createProject;
    }

    const data: createOrUpdateProjectRequest = await graphQlClient.request(
      ProjectsGraphQL.mutations.createProject,
      { project: normalizedPayload },
    );
    if (!data?.createProject) throw new Error('Failed to create project');
    return data.createProject;
  },

  async updateProject(payload: UpdateProjectPayload): Promise<Project> {
    if (!payload.projectId) throw new Error('projectId is required');

    // Normalize empty-string ids to null to avoid backend UUID parsing errors
    const normalizedPayload: UpdateProjectPayload = {
      ...payload,
      clientId: (payload as any).clientId || null,
      projectManagerId: (payload as any).projectManagerId || null,
      superintendentIds: Array.isArray((payload as any).superintendentIds)
        ? ((payload as any).superintendentIds as string[]).filter(
            (id) => !!id && id !== '',
          )
        : [],
      reviewerEmployeeIds: Array.isArray((payload as any).reviewerEmployeeIds)
        ? ((payload as any).reviewerEmployeeIds as string[]).filter(
            (id) => !!id && id !== '',
          )
        : [],
    } as any;

    if (normalizedPayload.siteImage) {
      const { siteImage, ...rest } = normalizedPayload as any;
      const data: createOrUpdateProjectRequest = await graphQlUpload(
        siteImage,
        'project',
        ProjectsGraphQL.mutations.updateProject,
        { ...rest },
      );
      if (!data?.updateProject) throw new Error('Failed to update project');
      return data.updateProject;
    }

    const data: createOrUpdateProjectRequest = await graphQlClient.request(
      ProjectsGraphQL.mutations.updateProject,
      { project: normalizedPayload },
    );
    if (!data?.updateProject) throw new Error('Failed to update project');
    return data.updateProject;
  },

  async deleteProject(
    projectId: string,
  ): Promise<{ affected: number; messages?: string[] }> {
    if (!projectId) throw new Error('projectId is required');

    const data: any = await graphQlClient.request(
      ProjectsGraphQL.mutations.deleteProject,
      { id: projectId },
    );

    return data?.deleteProject as { affected: number; messages?: string[] };
  },

  async updateProjectStatus(
    projectId: string,
    status: string,
  ): Promise<{ projectId: string; name: string; state: string }> {
    if (!projectId) throw new Error('projectId is required');
    if (!status) throw new Error('status is required');

    const data: any = await graphQlClient.request(
      ProjectsGraphQL.mutations.updateProjectStatus,
      { projectId, status },
    );

    if (!data?.updateProjectStatus) throw new Error('Failed to update project status');
    return data.updateProjectStatus;
  },

  async toggleProjectFavorite(projectId: string): Promise<boolean> {
    if (!projectId) throw new Error('projectId is required');

    const data: { toggleProjectFavorite: boolean } = await graphQlClient.request(
      ProjectsGraphQL.mutations.toggleProjectFavorite,
      { projectId },
    );

    return data.toggleProjectFavorite;
  },
};

export default projectService;
